import {
  centsToEuros,
  formatCents,
  formatCount,
  formatDay,
  parseMoneyToCents,
  tt,
} from '@agos/shared'

/**
 * The platform's invoices to its franchises.
 *
 * **Overdue is derived, never read from the status — FB-S03.** Legacy counts
 * `invoices.filter(i => i.status === "overdue").length`, and nothing ever assigns that status:
 * `PlatformInvoiceService` lists it among the values it will *accept* but no job, query or trigger
 * moves an invoice there when `due_at` passes. Measured, all **6** invoices are `issued`, unpaid and
 * past due — **3 434,16 €** — and the tile reads 0, in neutral grey.
 *
 * The server does this correctly elsewhere: `FinancialSummary` computes
 * `WHEN i.due_date < CURRENT_DATE THEN 'overdue'` in SQL for the restaurant's supplier invoices. The
 * platform's own invoices are the one place it is not done.
 *
 * `amount` arrives as a **decimal string** — `"749.07"` — so it is euros and does go through
 * `parseMoneyToCents`, unlike the usage report's `costCents`/`revenueCents`. Two adjacent endpoints,
 * two units; each is read as what it is.
 */

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'void'

export interface PlatformInvoice {
  id: number
  invoiceNumber: string
  franchiseName: string
  franchiseSlug: string
  amountCents: number
  status: InvoiceStatus
  issuedAt: string | null
  dueAt: string | null
  paidAt: string | null
  /** `null` means it has never been e-mailed — which is every invoice on this data. */
  sentAt: string | null
  /**
   * The month this invoice covers, and the three fields that make it payable - `SAAS.md` §3.44.
   *
   * **All five were answered and none was read.** §3.39 softens that - the operator can open the PDF
   * now, and the document carries everything - but *which month is this* and *what is the variable
   * symbol* are the two things somebody is asked on the telephone, and opening a document to answer
   * them is worse than a line of text.
   *
   * `sentTo` answers a question nothing else does: §3.8 guards the resend by naming the recipient
   * from a **settings** lookup, while the invoice records who it actually went to.
   */
  periodStart: string | null
  periodEnd: string | null
  vatAmountCents: number
  /**
   * The net, in cents - §3.126.
   *
   * **`INVOICE_COLUMNS` has answered `i.subtotal` since the beginning and this interface never declared
   * it.** `InvoiceFieldsAgreeTest` exempted it on the reasoning that legacy's list shows the total alone
   * and its forms compute the net from the line items - true then, and §3.126 makes it false: the net is
   * now the one figure an operator types, so the editor has to open on the stored one. The exemption is
   * gone rather than reworded.
   */
  subtotalCents: number
  variableSymbol: string | null
  sentTo: string | null
  /**
   * The Slovak *dátum dodania* - the date of supply, which VAT is reckoned from - `SAAS.md §3.97`.
   *
   * **The server has always answered this and this interface never declared it**, so no census that
   * reads the interface could see it missing and nothing drew it. `InvoiceRenderer` prints
   * `firstOf(invoice, "delivery_date", "period_end")` onto the document, and legacy has an editor for
   * it; AGOS could neither show nor set it.
   */
  deliveryDate: string | null
  /** The rate the document charges. Writable on the PATCH, and §3.97 is what gave it a control. */
  vatRatePct: number
  /**
   * The note on the document - §3.121, and §3.97's shape exactly.
   *
   * **`INVOICE_COLUMNS` answers `i.notes` on every row and this interface never declared it**, so no
   * census reading the interface could see it missing and nothing drew it. The manual-create form has
   * always *sent* one; only the editor could not change it, and nothing could read it back.
   *
   * It is the only field on the row that can hold **why** an invoice was voided, which is what makes
   * it load-bearing rather than decoration.
   */
  notes: string | null
  /**
   * Whether the stored subtotal is what the lines come to - §3.124, the owner's condition on #31.
   *
   * **`null` carries information and must not collapse to `false`.** §3.110 chose it deliberately:
   * there were no lines to compare, or a line whose own figures could not be read, and answering
   * `false` there would assert a disagreement the server cannot check. A screen drawing `null` as
   * *agrees* would undo that decision one layer up.
   *
   * **The server has computed this since §3.110 and nothing read it**, so the owner's *"it must say so
   * on the invoice"* was unmet from the day it was recorded as met.
   */
  totalMatchesLines: boolean | null
}

const STATUSES: readonly string[] = ['draft', 'issued', 'paid', 'overdue', 'void']

/**
 * The statuses an operator may set - §3.121. **Four of the five, and the omission is the decision.**
 *
 * `overdue` is deliberately absent. **FB-S03** is the entry that took it out of the tile: *"legacy's
 * overdue tile counts `status === "overdue"`, a status nothing ever assigns, so it reads 0 in neutral
 * grey while every invoice on the platform is unpaid and past due"*. AGOS derives it in
 * `effectiveStatus` from `paidAt` and `dueAt`, so a settable `overdue` would let somebody freeze a
 * wrong answer into the column the derivation exists to replace. **Legacy's select offers all five;
 * porting it faithfully would be the defect.**
 *
 * The server accepts `overdue` and §10A.234 says why - the owner's restored data carries legacy rows
 * with it, and a guard that cannot round-trip existing data breaks the screen.
 */
export const SETTABLE_STATUSES: readonly InvoiceStatus[] = ['draft', 'issued', 'paid', 'void']

export function invoicesOf(payload: unknown): PlatformInvoice[] {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(row.invoices) ? row.invoices : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const invoice = entry as Record<string, unknown>
    const text = (key: string): string | null => {
      const value = invoice[key]
      return typeof value === 'string' && value.trim() !== '' ? value : null
    }
    return [
      {
        id: typeof invoice.id === 'number' ? invoice.id : 0,
        invoiceNumber: text('invoiceNumber') ?? '',
        franchiseName: text('franchiseName') ?? '',
        franchiseSlug: text('franchiseSlug') ?? '',
        amountCents: parseMoneyToCents(invoice.amount as string | number | null),
        // An unrecognised status is `draft` rather than `paid`: the safe direction is *not yet settled*.
        status: STATUSES.includes(String(invoice.status)) ? (invoice.status as InvoiceStatus) : 'draft',
        // §3.121 - answered by `INVOICE_COLUMNS` all along; `null` is *nothing was written*, which is
        // a different fact from an empty string and is what a cleared note sends back.
        notes: typeof invoice.notes === 'string' ? invoice.notes : null,
        // §3.126 - a decimal string on the wire, like `amount`, so it goes through the same reader.
        subtotalCents: parseMoneyToCents(invoice.subtotal as string | number | null),
        // §3.124 - a `Boolean` on the wire, so anything that is not a boolean is *nothing to compare*.
        totalMatchesLines:
          typeof invoice.totalMatchesLines === 'boolean' ? invoice.totalMatchesLines : null,
        issuedAt: text('issuedAt'),
        dueAt: text('dueAt'),
        paidAt: text('paidAt'),
        periodStart: text('periodStart'),
        deliveryDate: text('deliveryDate'),
        // Not `parseMoneyToCents`: a percentage is not money, and 20 % must not become 2 000.
        vatRatePct: Number(invoice.vatRatePct ?? 0) || 0,
        periodEnd: text('periodEnd'),
        vatAmountCents: parseMoneyToCents(invoice.vatAmount as string | number | null),
        variableSymbol: text('variableSymbol'),
        sentTo: text('sentTo'),
        sentAt: text('sentAt'),
      },
    ]
  })
}

export function isPaid(invoice: PlatformInvoice): boolean {
  return invoice.paidAt !== null || invoice.status === 'paid'
}

/** Neither paid nor written off. */
export function isOutstanding(invoice: PlatformInvoice): boolean {
  return !isPaid(invoice) && invoice.status !== 'void' && invoice.status !== 'draft'
}

/**
 * Past its due date and unpaid — computed, because the status will not say so.
 *
 * The `status === 'overdue'` case is still honoured, for an invoice somebody marked by hand.
 */
export function isOverdue(invoice: PlatformInvoice, now: Date): boolean {
  if (!isOutstanding(invoice)) return false
  if (invoice.status === 'overdue') return true
  if (invoice.dueAt === null) return false
  const due = Date.parse(invoice.dueAt)
  return Number.isFinite(due) && due < now.getTime()
}

export interface BillingTotals {
  outstandingCents: number
  overdueCount: number
  overdueCents: number
  /**
   * What the platform has billed this calendar month - §3.183, FB-957, the owner's own report.
   *
   * **This was on legacy's screen and on none of AGOS's.** A billing run's whole output is *this month's
   * invoices*, and after pressing *Vyfakturovať* the only thing that moved was a one-line report - so the
   * figure that says whether the platform billed anything was nowhere, on the screen whose subject it is.
   *
   * **The calendar month rather than a rolling thirty days**, because that is the period a run covers and
   * what legacy's own tile sums. Over `issuedAt`, which is when the document was raised: `periodStart` is
   * what it bills *for*, and a run on the first of October raises September's invoices - so summing by
   * period would answer *nothing this month* on the very day the run happened.
   *
   * **Every status counts, including `void`**, and that is deliberate: this answers *did the run produce
   * documents*, not *what will be collected*, which is `outstandingCents` two fields up. A voided invoice
   * was still raised.
   */
  thisMonthCents: number
  thisMonthCount: number
}

export function billingTotals(invoices: readonly PlatformInvoice[], now: Date): BillingTotals {
  const outstanding = invoices.filter(isOutstanding)
  const overdue = outstanding.filter((invoice) => isOverdue(invoice, now))
  const thisMonth = invoices.filter((invoice) => isInMonthOf(invoice.issuedAt, now))
  return {
    // Plain integer sums: these are already cents (FB-056).
    outstandingCents: outstanding.reduce((total, invoice) => total + invoice.amountCents, 0),
    overdueCount: overdue.length,
    overdueCents: overdue.reduce((total, invoice) => total + invoice.amountCents, 0),
    thisMonthCents: thisMonth.reduce((total, invoice) => total + invoice.amountCents, 0),
    thisMonthCount: thisMonth.length,
  }
}

/**
 * Whether an ISO timestamp falls in `now`'s own calendar month - §3.183.
 *
 * **Compared as year and month rather than against a month-start string**, which is what legacy does
 * (`i.issuedAt >= monthStart`) and what makes its tile wrong in one direction: a string comparison
 * against a **local** `toISOString()` boundary counts an invoice raised in the last hours of the previous
 * month whenever the viewer is east of UTC. Reading the parsed date's own year and month cannot.
 *
 * An unparseable or absent timestamp is **not** in the month: a figure labelled *this month* must not
 * absorb a row whose date nothing could read.
 */
function isInMonthOf(iso: string | null, now: Date): boolean {
  if (iso === null) return false
  const raised = new Date(iso)
  if (Number.isNaN(raised.getTime())) return false
  return raised.getFullYear() === now.getFullYear() && raised.getMonth() === now.getMonth()
}

/** How late, in days. Zero for anything not overdue. */
export function daysOverdue(invoice: PlatformInvoice, now: Date): number {
  if (!isOverdue(invoice, now) || invoice.dueAt === null) return 0
  const due = Date.parse(invoice.dueAt)
  if (!Number.isFinite(due)) return 0
  return Math.max(0, Math.floor((now.getTime() - due) / 86_400_000))
}

export function overdueNote(totals: BillingTotals): string {
  if (totals.overdueCount === 0) return ''
  return `${formatCount(totals.overdueCount)} ${tt('faktúr po splatnosti')} · ${formatCents(totals.overdueCents)}`
}

// ─── whether an invoice can be delivered at all — FB-S05 ─────────────────────

/**
 * The top-level domains a billing address is allowed to end in.
 *
 * Not a validity check on the address as a whole — that is `@Email`'s job and the server's. This catches
 * the one failure that is invisible on every screen: a domain that looks right and cannot receive.
 * Measured, the franchise owing 2 660,49 € of the 3 434,16 € outstanding is configured as
 * `…@gmail.som`.
 *
 * The list is deliberately short and common rather than exhaustive: an address ending in something not
 * here is reported as *worth checking*, never as invalid, because the day somebody uses a real
 * `.restaurant` address this must not lock them out of their own invoices.
 */
const COMMON_TLDS = new Set([
  'sk', 'cz', 'com', 'eu', 'org', 'net', 'info', 'biz', 'at', 'hu', 'pl', 'de', 'io', 'app', 'email',
])

export type DeliveryProblem = 'missing' | 'suspicious-domain' | null

/** Why an invoice to this address may not arrive, or `null` when nothing is visibly wrong. */
export function deliveryProblem(billingEmail: string | null): DeliveryProblem {
  const address = (billingEmail ?? '').trim()
  // `BillingRunner` skips the send entirely on a blank address — nothing is attempted at all.
  if (address === '') return 'missing'
  const tld = address.split('@').at(-1)?.split('.').at(-1)?.toLowerCase() ?? ''
  if (tld === '' || !COMMON_TLDS.has(tld)) return 'suspicious-domain'
  return null
}

export function deliveryNote(problem: DeliveryProblem): string {
  if (problem === 'missing') return tt('Franšíza nemá fakturačný e-mail - faktúra sa neodosiela.')
  if (problem === 'suspicious-domain') return tt('Fakturačný e-mail má nezvyčajnú doménu - overte ho.')
  return ''
}

/** Whether this invoice has ever left the building. */
export function wasSent(invoice: PlatformInvoice): boolean {
  return invoice.sentAt !== null
}

export function unsentCount(invoices: readonly PlatformInvoice[]): number {
  return invoices.filter((invoice) => isOutstanding(invoice) && !wasSent(invoice)).length
}

/**
 * Said above the list when money is owed on invoices nobody has been sent.
 *
 * The overdue counter and the unsent count are the same silence from two directions: nobody was told
 * they owed anything, and no screen said nobody had been told.
 */
export function unsentNote(invoices: readonly PlatformInvoice[]): string {
  const unsent = unsentCount(invoices)
  if (unsent === 0) return ''
  return `${formatCount(unsent)} ${tt('nezaplatených faktúr nebolo nikdy odoslaných.')}`
}

/**
 * Overdue, oldest due date first, and the largest amount first within a date.
 *
 * Four of the six share two due dates, so without the second key the order would fall out of whatever
 * the server happened to send — stable, but arbitrary. Chasing the biggest debt of the oldest date
 * first is a rule somebody can act on.
 */
export function chaseOrder(invoices: readonly PlatformInvoice[], now: Date): PlatformInvoice[] {
  return invoices
    .filter((invoice) => isOverdue(invoice, now))
    .sort((left, right) => {
      const byDate = (left.dueAt ?? '').localeCompare(right.dueAt ?? '')
      return byDate !== 0 ? byDate : right.amountCents - left.amountCents
    })
}

// ─── §3.8 - the three writes that end a chase ────────────────────────────────
//
// FB-505: *"the platform's own billing is a report. The overdue tile FB-S03 fixed counts six unpaid
// invoices that cannot be resent from here."* Five write endpoints, none called - and
// `PlatformInvoiceService.update` carries a comment about *"the button on the screen"* that does not
// exist.

/**
 * The body that marks an invoice paid.
 *
 * **No `paidAt`.** The server stamps `now()` when the status arrives as `paid` with no timestamp, and
 * its own comment says that is *"the only way it can know"*. A browser sending its own clock would
 * put the operator's timezone on a tax document, and a machine an hour out would date a payment
 * before it happened.
 */
export function markPaidBody(): { status: string } {
  return { status: 'paid' }
}

/** Whether marking this one paid would do anything. */
export function mayMarkPaid(invoice: PlatformInvoice): boolean {
  return !isPaid(invoice)
}

/**
 * Who a resend would go to, or the empty string when there is nobody.
 *
 * `InvoiceDelivery.resend` falls back to the franchise's `billing_email` when the request names no
 * recipient, and **skips the send entirely on a blank one** - which `deliveryNote` already says on
 * the row. So the confirmation can name the address before the click rather than reporting an
 * attempt that never happened.
 */
export function resendRecipient(billingEmail: string | null | undefined): string {
  return (billingEmail ?? '').trim()
}

/**
 * What the confirmation says before one e-mail goes to one franchise.
 *
 * **The recipient, not a count** - §9.339's guard needed a count and a cost because it starts a
 * recurring charge; this is a single mail, so *who* is the fact that matters.
 */
export function resendConfirmation(invoice: PlatformInvoice, billingEmail: string | null): string {
  const to = resendRecipient(billingEmail)
  if (to === '') {
    return tt(
      'Táto franšíza nemá fakturačný e-mail, takže sa nepošle nič. Doplňte ho v karte franšízy.',
    )
  }
  return `${tt('Faktúra')} ${invoice.invoiceNumber ?? invoice.id} ${tt('sa znova pošle na')} ${to}.`
}

/** A resend with nobody to send to is refused before it is attempted. */
export function mayResend(billingEmail: string | null | undefined): boolean {
  return resendRecipient(billingEmail) !== ''
}

/**
 * The period a billing run would bill, defaulting to the month just finished.
 *
 * **Not the running month.** Billing a month before it has ended invoices a franchise for days they
 * have not had, and the run is the thing that raises a tax document. The first of the month is when
 * somebody presses this, so the previous month is what they mean.
 */
export function defaultBillingPeriod(now: Date): { year: number; month: number } {
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { year: previous.getFullYear(), month: previous.getMonth() + 1 }
}

/**
 * The first year this platform could have billed anything - §3.183.
 *
 * A floor rather than no floor, because the control beside this is a number input on a screen that
 * raises tax documents: `202` and `20267` are one keystroke from `2026`, and a run for year 202 answers
 * *nothing to bill* rather than *that is not a year*, which reads as the feature being broken.
 */
export const FIRST_BILLABLE_YEAR = 2024

/** The twelve months, so the control is a select rather than a box that can hold 13 - §3.183. */
export const MONTHS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

/**
 * Why a hand-typed billing period cannot be run, or null - §3.183, FB-957.
 *
 * **The screen could bill the previous month and no other, ever.** `defaultBillingPeriod` is the only
 * period it ever sent, so a month the scheduler missed - and production pins `AGOS_JOBS_ENABLED=false`
 * (§10A.278) - could not be invoiced from the product at all, while `POST /super-admin/billing/run` has
 * taken `{year, month}` since it was written. Legacy's page has a year input and a month input.
 *
 * **A future period is refused rather than run.** A franchise's entitlement is read at run time, so
 * billing November in October raises invoices for a month nobody has had - and the server would do it,
 * because the guard it does carry is the double-billing one (*"a franchise already invoiced for the
 * period is counted as skipped"*), which says nothing about *when*.
 *
 * **The current month is allowed**, deliberately: a franchise created mid-month and billed early is a
 * decision an operator may need to make, and the run is idempotent per franchise and period.
 */
export function billingPeriodProblem(
  year: number,
  month: number,
  now: Date,
): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month)) return tt('Rok a mesiac musia byť čísla.')
  if (month < 1 || month > 12) return tt('Mesiac musí byť od 1 do 12.')
  if (year < FIRST_BILLABLE_YEAR) {
    return `${tt('Najstarší rok, ktorý sa dá vyfakturovať, je')} ${FIRST_BILLABLE_YEAR}.`
  }
  if (year > now.getFullYear()) return tt('Budúci rok sa fakturovať nedá.')
  if (year === now.getFullYear() && month > now.getMonth() + 1) {
    return tt('Budúci mesiac sa fakturovať nedá - nárok na faktúru sa počíta až za uplynulé obdobie.')
  }
  return null
}

/**
 * What the confirmation says before a run.
 *
 * It names the period, and it says the thing that makes the button safe to offer at all - the
 * controller's own note: *"safe to press twice: a franchise already invoiced for the period is
 * counted as skipped."*
 */
export function billingRunConfirmation(period: { year: number; month: number }): string {
  return `${tt('Vystaví faktúry za')} ${String(period.month).padStart(2, '0')}/${period.year}. ${tt('Franšízy, ktoré už za toto obdobie faktúru majú, sa preskočia - spustiť dvakrát je bezpečné.')}`
}

/** What a finished run achieved, from whatever the server answered. */
export function billingRunReport(payload: unknown): string {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >
  const created = typeof row.created === 'number' ? row.created : 0
  const skipped = typeof row.skipped === 'number' ? row.skipped : 0
  if (created === 0 && skipped === 0) {
    // Nought and nought is a real answer - no franchise was due - and saying *done* would leave
    // somebody wondering whether it ran.
    return tt('Nebolo čo fakturovať - žiadna franšíza za toto obdobie nemá nárok na faktúru.')
  }
  return `${tt('Vystavené')}: ${created}. ${tt('Preskočené (už majú faktúru)')}: ${skipped}.`
}

/*
 * ── Raising one by hand - SAAS.md §3.19, the owner's #16: *"Raise yes, delete never."* ──────────
 */

/** The server's own limits, so the form refuses what `validate` would refuse. */
const DESCRIPTION_LIMIT = 300
const UNITS_LIMIT = 20
const NOTES_LIMIT = 1000

/** A line as the form holds it: text, because these are inputs. */
export interface ManualInvoiceLine {
  description: string
  units: string
  quantity: string
  unitPrice: string
}

export interface ManualInvoiceDraft {
  franchiseId: number | null
  lines: ManualInvoiceLine[]
  notes: string
}

export function emptyManualLine(): ManualInvoiceLine {
  return { description: '', units: '', quantity: '1', unitPrice: '' }
}

export function emptyManualInvoice(): ManualInvoiceDraft {
  return { franchiseId: null, lines: [emptyManualLine()], notes: '' }
}

/** A typed number, or `null` when it is not one. Accepts the comma a Slovak keyboard produces. */
function numberOf(text: string): number | null {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const value = Number(trimmed.replace(',', '.'))
  return Number.isFinite(value) ? value : null
}

/**
 * A line's total, in cents, derived from quantity x unit price.
 *
 * **This is the field the form does not offer.** `PlatformInvoiceService` sums the lines' own
 * `total` values into the invoice subtotal, and it accepts whatever arrives - so a line reading
 * *2 x 5,00 = 999,00* would be billed as sent. The server has always allowed that and legacy does
 * too, so nothing is refused that used to pass; the screen simply cannot express it.
 */
export function manualLineTotalCents(line: ManualInvoiceLine): number {
  const quantity = numberOf(line.quantity)
  const unitPrice = numberOf(line.unitPrice)
  if (quantity === null || unitPrice === null) return 0
  const exact = exactProductCents(line.quantity, line.unitPrice)
  return exact ?? parseMoneyToCents(quantity * unitPrice)
}

/** A plain decimal, optionally signed, with either separator. Anything else is not handled here. */
const PLAIN_DECIMAL = /^[+-]?\d*[.,]?\d*$/

/**
 * `quantity x unitPrice` in cents, computed exactly, or `null` when the input is not a plain
 * decimal - SAAS.md §3.73.
 *
 * **This exists because the float version disagreed with the document on 2 % of ordinary lines.**
 * `2,5` at `0,09` is `0.22499999999999998` as an IEEE-754 double, so `Math.round` gives 22 cents
 * while the exact product `0.225` rounds to 23 - and 23 is what the server stores, because
 * `PlatformInvoiceService.lineItems` derives the line total with `BigDecimal` and `HALF_UP` and
 * ignores what the client sent. Measured, not reasoned about: 3 203 disagreements in 160 000
 * two-decimal cases.
 *
 * Each side is parsed to a mantissa and a scale, the mantissas are multiplied as `BigInt` so no
 * precision is lost at any width, and the division to cents rounds **half away from zero** - which
 * is what `HALF_UP` means, and identical to half-up for the positive-only values §10A.130 permits.
 *
 * **`parseMoneyToCents` is deliberately left alone.** It is the whole project's money parser,
 * `Math.round` is right for a single value, and the defect is in multiplying two of them. The
 * fallback above keeps it for the inputs this cannot parse - an exponent form, say - so nothing that
 * used to produce a figure stops producing one.
 */
function exactProductCents(quantity: string, unitPrice: string): number | null {
  const left = mantissaAndScale(quantity)
  const right = mantissaAndScale(unitPrice)
  if (left === null || right === null) return null

  const scaled = left.mantissa * right.mantissa * 100n
  const divisor = 10n ** (left.scale + right.scale)
  const negative = scaled < 0n
  const magnitude = negative ? -scaled : scaled
  const whole = magnitude / divisor
  const remainder = magnitude % divisor
  const rounded = whole + (remainder * 2n >= divisor ? 1n : 0n)
  return Number(negative ? -rounded : rounded)
}

function mantissaAndScale(value: string): { mantissa: bigint; scale: bigint } | null {
  const trimmed = value.trim()
  if (trimmed === '' || !PLAIN_DECIMAL.test(trimmed)) return null
  const sign = trimmed.startsWith('-') ? -1n : 1n
  const digits = trimmed.replace(/^[+-]/, '').replace(',', '.')
  const [whole = '', fraction = ''] = digits.split('.')
  const joined = `${whole}${fraction}`
  if (joined === '') return null
  return { mantissa: sign * BigInt(joined), scale: BigInt(fraction.length) }
}

/** The lines' totals, which is what the server will use as the subtotal. */
export function manualSubtotalCents(draft: ManualInvoiceDraft): number {
  return draft.lines.reduce((sum, line) => sum + manualLineTotalCents(line), 0)
}

/**
 * VAT and the total, by the same arithmetic `create` uses.
 *
 * `subtotal.multiply(vatRate).movePointLeft(2)` rounded half up, then added - so the confirmation
 * shows the figure that will be on the document rather than an approximation of it.
 */
export function manualTotalsCents(
  draft: ManualInvoiceDraft,
  vatRatePct: number,
): { subtotalCents: number; vatCents: number; totalCents: number } {
  const subtotalCents = manualSubtotalCents(draft)
  const vatCents = Math.round((subtotalCents * vatRatePct) / 100)
  return { subtotalCents, vatCents, totalCents: subtotalCents + vatCents }
}

/**
 * Why this draft cannot be sent, or `null` when it can.
 *
 * Mirrors `PlatformInvoiceService.validate(body, true)` rather than inventing a second rulebook: a
 * franchise, at least one line, and on every line a description within 300 characters and three
 * numbers. The one thing it adds is a **positive** quantity and price, because a zero line is a
 * line somebody forgot to fill in and the server would accept it.
 */
export function manualInvoiceProblem(draft: ManualInvoiceDraft): string | null {
  if (draft.franchiseId === null) return tt('Vyberte franšízu.')
  if (draft.lines.length === 0) return tt('Faktúra musí mať aspoň jednu položku.')
  if (draft.notes.length > NOTES_LIMIT) return tt('Poznámka je príliš dlhá.')
  for (const [index, line] of draft.lines.entries()) {
    const where = `${tt('Položka')} ${index + 1}: `
    if (line.description.trim() === '') return where + tt('doplňte popis.')
    if (line.description.length > DESCRIPTION_LIMIT) return where + tt('popis je príliš dlhý.')
    if (line.units.length > UNITS_LIMIT) return where + tt('jednotka je príliš dlhá.')
    const quantity = numberOf(line.quantity)
    const unitPrice = numberOf(line.unitPrice)
    if (quantity === null || unitPrice === null) return where + tt('počet a cena musia byť čísla.')
    if (quantity <= 0) return where + tt('počet musí byť väčší ako nula.')
    if (unitPrice <= 0) return where + tt('cena musí byť väčšia ako nula.')
  }
  return null
}

/**
 * The body `POST /super-admin/invoices` is sent.
 *
 * **Two fields and nothing else.** No invoice number, no variable symbol, no VAT rate, no period,
 * no total: every one of those is derived by the server, and sending a browser's version of any of
 * them is how the document and its own numbering come to disagree. `total` per line is derived
 * here, from the two figures the operator actually typed.
 */
export function manualInvoiceBody(draft: ManualInvoiceDraft): {
  franchiseId: number
  lineItems: { description: string; units?: string; quantity: number; unitPrice: number; total: number }[]
  notes?: string
} {
  const body: ReturnType<typeof manualInvoiceBody> = {
    franchiseId: draft.franchiseId ?? 0,
    lineItems: draft.lines.map((line) => {
      const quantity = numberOf(line.quantity) ?? 0
      const unitPrice = numberOf(line.unitPrice) ?? 0
      return {
        description: line.description.trim(),
        ...(line.units.trim() === '' ? {} : { units: line.units.trim() }),
        quantity,
        unitPrice,
        total: centsToEuros(manualLineTotalCents(line)),
      }
    }),
  }
  if (draft.notes.trim() !== '') body.notes = draft.notes.trim()
  return body
}

/**
 * What the confirmation says before a tax document exists.
 *
 * The other guarded writes name a recipient or a period. **This one names what will be on the
 * document** - the franchise, how many lines, the total with VAT - and then the fact that separates
 * it from the other five: it issues, and the number comes from the sequence and cannot be handed
 * back. Deleting is not offered at all (§3.19), so *void* is the way out of a mistake.
 */
export function manualInvoiceConfirmation(
  draft: ManualInvoiceDraft,
  franchiseName: string,
  vatRatePct: number,
): string {
  const { totalCents } = manualTotalsCents(draft, vatRatePct)
  // `formatCount` is a number formatter, not a pluraliser - there is no plural helper in `shared`,
  // and inventing one for a confirmation string is not where that decision belongs.
  const lines = `${formatCount(draft.lines.length)} ${draft.lines.length === 1 ? tt('položka') : tt('položiek')}`
  return `${tt('Vystaví daňový doklad pre')} ${franchiseName}: ${lines}, ${formatCents(totalCents)} ${tt('s DPH')}. ${tt('Číslo faktúry sa priradí zo sekvencie a nedá sa vrátiť - chybnú faktúru stornujete stavom «void».')}`
}

/**
 * The month an invoice covers - §3.44.
 *
 * **Absent is a real state and reads as such.** A manual invoice raised through §3.19 takes a period
 * from the server, but `PATCH` can leave one without; and rendering an empty range as `-` invites the
 * reader to think the invoice covers nothing rather than that nobody said.
 *
 * Dates only, because both ends are midnight and a time on an invoice period is noise.
 */
export function periodNote(invoice: PlatformInvoice): string {
  if (invoice.periodStart === null || invoice.periodEnd === null) return ''
  return `${formatDay(invoice.periodStart)} - ${formatDay(invoice.periodEnd)}`
}

/**
 * What the downloaded invoice is called - `SAAS.md` §3.39.
 *
 * **Legacy names it `invoice-{id}.pdf`, and the id is a database key.** The invoice *number* is what
 * the document says, what the franchise quotes when it pays and what an accountant files it under -
 * `MM202609-0001` rather than `7`. So the number wins where there is one.
 *
 * **A draft with no number still has to be openable**, which is why there is a fallback rather than a
 * refusal: `PATCH` can clear `invoice_number`, and an invoice you cannot look at is how a wrong one
 * stays wrong.
 */
export function pdfFilename(invoice: { id: number; invoiceNumber: string }): string {
  const number = invoice.invoiceNumber.trim()
  return number === '' ? `faktura-${invoice.id}.pdf` : `faktura-${number}.pdf`
}

/**
 * Filtering and sorting the invoice list - `SAAS.md §3.84`.
 *
 * **Client-side, exactly as legacy is**, and that is a fact about the endpoint rather than a choice:
 * `GET /super-admin/billing/invoices` declares no `from`/`to` (unlike `/profitability`,
 * `/cardpay/daily` and the usage report, which all do), so the rows arrive whole and the screen
 * narrows them. Legacy's `Billing.tsx` does the same in one `useMemo`.
 *
 * Pure, and in this file rather than in the component, because *which invoices am I looking at* is
 * the question the operator will dispute and a rule that only exists inside a component is a rule
 * nobody can check.
 */
export type InvoiceSortKey = 'issuedAt' | 'amount' | 'franchiseName' | 'status' | 'dueAt'

export interface InvoiceSort {
  key: InvoiceSortKey
  dir: 'asc' | 'desc'
}

/** Newest issued first - legacy's default, and the order somebody chasing payment wants. */
export const DEFAULT_INVOICE_SORT: InvoiceSort = { key: 'issuedAt', dir: 'desc' }

export interface InvoiceFilter {
  /** A status slug, or `'all'`. */
  status: string
  /** Matched against the franchise name, the invoice number and the variable symbol. */
  search: string
  /** Bare calendar dates, compared against `issuedAt`. Empty means unbounded. */
  from: string
  to: string
}

export const EMPTY_INVOICE_FILTER: InvoiceFilter = { status: 'all', search: '', from: '', to: '' }

function sortValue(invoice: PlatformInvoice, key: InvoiceSortKey): string | number {
  if (key === 'amount') return invoice.amountCents
  if (key === 'franchiseName') return invoice.franchiseName
  if (key === 'status') return invoice.status
  if (key === 'dueAt') return invoice.dueAt ?? ''
  return invoice.issuedAt ?? ''
}

/**
 * The rows the screen should draw.
 *
 * **The date bounds compare a timestamp against a bare date, on purpose.** `issuedAt` is an ISO
 * instant and `from` is `2026-09-01`; `'2026-09-01T08:00:00Z' >= '2026-09-01'` is true because the
 * date is a prefix, so a lexical comparison is the correct one here and is what legacy does. The
 * upper bound therefore has to reach the end of its day - `to + 'T23:59:59'` - or choosing the same
 * day at both ends returns nothing at all, which reads as *the filter is broken*.
 *
 * **A row with no `issuedAt` is kept when no bound is set and dropped when one is.** A draft that has
 * never been issued has no date to be inside a period, and silently including it would put it in
 * every window the operator asks about.
 */
export function filterAndSortInvoices(
  invoices: readonly PlatformInvoice[],
  filter: InvoiceFilter,
  sort: InvoiceSort = DEFAULT_INVOICE_SORT,
): PlatformInvoice[] {
  let rows = [...invoices]

  if (filter.status !== 'all') {
    rows = rows.filter((invoice) => invoice.status === filter.status)
  }

  const query = filter.search.trim().toLowerCase()
  if (query !== '') {
    rows = rows.filter(
      (invoice) =>
        invoice.franchiseName.toLowerCase().includes(query) ||
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        (invoice.variableSymbol ?? '').toLowerCase().includes(query),
    )
  }

  if (filter.from !== '') {
    rows = rows.filter((invoice) => invoice.issuedAt !== null && invoice.issuedAt >= filter.from)
  }
  if (filter.to !== '') {
    const end = `${filter.to}T23:59:59`
    rows = rows.filter((invoice) => invoice.issuedAt !== null && invoice.issuedAt <= end)
  }

  return rows.sort((left, right) => {
    const first = sortValue(left, sort.key)
    const second = sortValue(right, sort.key)
    if (first < second) return sort.dir === 'asc' ? -1 : 1
    if (first > second) return sort.dir === 'asc' ? 1 : -1
    // A stable tie-break by id, so two invoices issued the same day do not swap places between
    // renders - which on a list somebody is ticking through reads as the screen moving by itself
    // (`agos-it-changed-by-itself-means-positional-state`).
    return left.id - right.id
  })
}

/** Clicking a column: the same one flips direction, a new one starts descending. */
export function nextInvoiceSort(current: InvoiceSort, key: InvoiceSortKey): InvoiceSort {
  if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  return { key, dir: 'desc' }
}

/** Says what the list is showing, so a filtered count is never mistaken for the whole archive. */
export function invoiceFilterNote(shown: number, total: number): string {
  if (shown === total) return `${tt('Faktúry')}: ${total}`
  return `${shown} ${tt('z')} ${total}`
}

/** Whether anything is narrowing the list - what the *clear* control keys off. */
export function invoiceFilterIsActive(filter: InvoiceFilter): boolean {
  return (
    filter.status !== 'all' || filter.search.trim() !== '' || filter.from !== '' || filter.to !== ''
  )
}

/**
 * The state an invoice is actually in, which is not always the state it is stored in - `SAAS.md §3.94`.
 *
 * **`overdue` is never assigned by the server.** This module already records that legacy's overdue tile
 * counts a status nothing writes, and `isOverdue` is what AGOS computes instead. So an invoice stored as
 * `issued` whose due date has passed **is** overdue, and a badge printing the stored value would say
 * *Vystavená* on a debt - while the filter offers an `overdue` option that no row could ever match.
 *
 * Taking `now` rather than reading the clock: every other date decision on this screen is passed one,
 * so a test can place an invoice either side of its due date.
 */
export function effectiveStatus(invoice: PlatformInvoice, now: Date): InvoiceStatus {
  return isOverdue(invoice, now) ? 'overdue' : invoice.status
}

/**
 * Said on a row whose total does not agree with its lines - §3.124, the owner's condition on #31.
 *
 * **Only the divergence.** A badge on every invoice reading *agrees* is a badge nobody reads, which is
 * §3.110's own argument for its two-cent tolerance. And **`null` is silence rather than a caveat**: an
 * invoice with no stored lines is the normal shape for a billing-run invoice, so a note on all of those
 * would bury the handful that matter.
 *
 * **It names what disagrees rather than saying *chyba*.** The operator's next question is *which figure
 * do I trust*, and the answer is that the lines are the record and the total was typed over them - which
 * is exactly the capability #31 sanctioned.
 */
export function totalDisagreesNote(invoice: PlatformInvoice): string {
  if (invoice.totalMatchesLines !== false) return ''
  return tt('Suma bola prepísaná - nesúhlasí so súčtom položiek.')
}

/** What each state is called, in the words the filter already uses - §3.94. */
export function invoiceStatusLabel(status: InvoiceStatus): string {
  if (status === 'draft') return tt('Návrh')
  if (status === 'issued') return tt('Vystavená')
  if (status === 'paid') return tt('Zaplatená')
  if (status === 'overdue') return tt('Po splatnosti')
  return tt('Zrušená')
}

/**
 * The badge colour, by what the state means rather than by the palette's order.
 *
 * Paid is settled, overdue is money owed late, void is a document that no longer counts, issued is
 * waiting, and a draft is not a claim on anybody yet.
 */
export function invoiceStatusColor(status: InvoiceStatus): 'green' | 'red' | 'zinc' | 'blue' | 'amber' {
  if (status === 'paid') return 'green'
  if (status === 'overdue') return 'red'
  if (status === 'void') return 'zinc'
  if (status === 'issued') return 'blue'
  return 'amber'
}

/**
 * *Vystavená 1. 9. 2026 · splatná 15. 9. 2026* - `SAAS.md §3.95`.
 *
 * **Both dates are sort keys and neither was on the row.** The sort offers *Dátum vystavenia* and
 * *Splatnosť*, and legacy has a column for each; AGOS surfaced `dueAt` only through `daysOverdue`,
 * which says nothing at all about an invoice that is not yet late. So a not-yet-due invoice could be
 * ordered by a date the reader could not see - the same defect §3.94 fixed for the status, on the same
 * screen and missed by the same census.
 *
 * <p>One line rather than two columns, because this row is prose and not a table: the screen is a
 * stack of invoice cards, and two of them read better as a clause than as a grid.
 *
 * <p><b>The due date is said even when it has passed</b>, beside the *Po splatnosti* badge and
 * `daysOverdue`'s count. The badge says *late*, the count says *how late*, and this says *since
 * when* - which is the one a franchise asks for on the telephone.
 */
export function invoiceDatesNote(invoice: PlatformInvoice): string {
  const issued = invoice.issuedAt === null ? '' : formatDay(invoice.issuedAt)
  const due = invoice.dueAt === null ? '' : formatDay(invoice.dueAt)
  if (issued === '' && due === '') return ''
  if (due === '') return `${tt('Vystavená')} ${issued}`
  if (issued === '') return `${tt('splatná')} ${due}`
  return `${tt('Vystavená')} ${issued} · ${tt('splatná')} ${due}`
}

/**
 * The five fields an invoice can be corrected in - `SAAS.md §3.97`.
 *
 * **Legacy's `EditInvoiceModal` has exactly these, and AGOS had no way to correct an invoice at all.**
 * `PATCH …/invoices/{id}` accepts twelve fields and the screen sent one - `{status: 'paid'}`.
 *
 * <p>`subtotal`, `vatAmount`, `amount` and `lineItems` are the other four the server takes and they are
 * **not here on purpose**: owner question #31 - *should an invoice's amount be correctable without its
 * lines?* - is open and is precisely about them. Legacy's own modal has no money field either, which is
 * what makes these five a port rather than a decision.
 *
 * <p>Dates as `YYYY-MM-DD` because that is what a date input reads and writes; the server parses them
 * as instants.
 */
export interface InvoiceEditDraft {
  invoiceNumber: string
  variableSymbol: string
  deliveryDate: string
  dueAt: string
  vatRatePct: string
  /**
   * §3.121 - the state, which the filter could narrow by and the editor could not set.
   *
   * `draft` and `void` were reachable by nothing at all: the manual-create form sends no status and
   * `issued` is the column's default, so the Tower could produce two of the five states its own filter
   * offers. `markPaid` covers `paid` and `overdue` is derived.
   */
  status: InvoiceStatus
  /** §3.121 - the note. Blank sends an explicit `null`, which is *nothing was written*. */
  notes: string
  /**
   * The net, in euro as typed - §3.126, owner answer #31.
   *
   * **One figure, and the VAT and the total are derived from it and the rate.** Legacy's two modals
   * compute all three in the browser and PATCH all three; the only difference is where the base comes
   * from - legacy sums its line items, and AGOS does not offer line editing because that would reverse
   * the server-prices-it decision `ManualInvoiceIsPricedByTheServerTest` holds.
   *
   * **Three free inputs would let four figures disagree**, `vatRatePct` included, and a tax document
   * whose VAT is not its rate applied to its base is not one anybody can defend. Deriving two leaves
   * exactly one thing that can diverge - the base against the lines - which is what §3.110 computes and
   * §3.124 prints.
   */
  subtotal: string
}

/** The form as the stored invoice fills it, with legacy's own fallback to the period's end. */
export function invoiceEditDraft(invoice: PlatformInvoice): InvoiceEditDraft {
  const day = (value: string | null): string => (value === null ? '' : value.slice(0, 10))
  return {
    invoiceNumber: invoice.invoiceNumber,
    variableSymbol: invoice.variableSymbol ?? '',
    // Legacy: `(invoice.deliveryDate ?? invoice.periodEnd).slice(0, 10)`. A billing-run invoice has no
    // delivery date of its own and the period's end is the date of supply, so the form opens on the
    // value the document already prints rather than on a blank a person has to guess.
    deliveryDate: day(invoice.deliveryDate ?? invoice.periodEnd),
    dueAt: day(invoice.dueAt ?? invoice.periodEnd),
    vatRatePct: String(invoice.vatRatePct),
    /* §3.121 - the stored status, already narrowed to one of the five by `invoicesOf`, which maps an
       unrecognised one to `draft`. So the select always opens on something it offers, even for a row
       the restored legacy data put an unknown status on. */
    status: invoice.status,
    notes: invoice.notes ?? '',
    // §3.126 - euro as the field holds them. The row carries cents, so this is the one place the two
    // units meet and it is spelled once.
    subtotal: (invoice.subtotalCents / 100).toFixed(2),
  }
}

/**
 * The VAT and the payable total a typed base comes to - §3.126, legacy's formula.
 *
 * `Billing.tsx`: `vatAmount = subtotal * vatRatePct / 100`, `total = subtotal + vatAmount`. Rounded to
 * the cent at each step, as the server does with `setScale(2, HALF_UP)` - a single rounding at the end
 * would differ by a cent on some rates and the document would disagree with itself by that cent.
 *
 * Returns cents, because that is what the screen formats and what the body sends after dividing back.
 */
export function derivedTotals(subtotal: string, vatRatePct: string): {
  subtotalCents: number
  vatAmountCents: number
  amountCents: number
} | null {
  /* **An empty string is refused explicitly, because `Number('')` is 0 rather than NaN.** Without this
     a blank base derived silently as an invoice for nought euro and saved one - `Number.isFinite(0)` is
     true. The same for the rate. Found by the case asserting this helper refuses what the server
     refuses, which is why that case was written from §10A.239's rules rather than from this code. */
  if (subtotal.trim() === '' || vatRatePct.trim() === '') {
    return null
  }
  const base = Number(subtotal.replace(',', '.'))
  const rate = Number(vatRatePct)
  if (!Number.isFinite(base) || base < 0 || !Number.isFinite(rate) || rate < 0 || rate > 100) {
    return null
  }
  const subtotalCents = Math.round(base * 100)
  const vatAmountCents = Math.round((subtotalCents * rate) / 100)
  return { subtotalCents, vatAmountCents, amountCents: subtotalCents + vatAmountCents }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

/** What is wrong with the form, said before the button rather than by the server afterwards. */
export function invoiceEditProblems(draft: InvoiceEditDraft): string[] {
  const found: string[] = []
  if (draft.invoiceNumber.trim() === '') {
    found.push(tt('Číslo faktúry nemôže byť prázdne.'))
  }
  for (const [value, label] of [
    [draft.deliveryDate, tt('Dátum dodania')],
    [draft.dueAt, tt('Dátum splatnosti')],
  ] as const) {
    if (value !== '' && !DAY.test(value)) found.push(`${label}: ${tt('neplatný dátum.')}`)
  }
  const vat = Number(draft.vatRatePct)
  if (draft.vatRatePct.trim() === '' || !Number.isFinite(vat) || vat < 0 || vat > 100) {
    found.push(tt('DPH musí byť medzi 0 a 100.'))
  }
  /* §3.121 - **voiding needs a reason.**
   *
   * A cancelled tax document whose reason is written down nowhere is worse than one not cancelled:
   * the row says *Zrušená* and the next person to ask why has the audit trail's *who* and no *what*.
   * `notes` is the only field on the row that can hold it, which is what makes this rule the reason
   * the note is editable at all rather than a nicety beside it.
   *
   * Only on the *transition*, not on every save of an already-void invoice - re-editing a row voided
   * last year must not demand a note somebody else should have written. */
  /* §3.126 - the base, **asked about on its own rather than through `derivedTotals`.**
   *
   * The first draft used that helper, which also refuses a rate outside 0-100 - so a single mistyped
   * rate produced **two** messages, one of them about a base that was perfectly fine. An existing case
   * caught it. One mistake, one sentence.
   *
   * The refusals are the server's own (§10A.239, built first for exactly this): not a number, or below
   * nought. A client rule the server does not share is a rule nobody can find - §3.25. */
  const base = Number(draft.subtotal.replace(',', '.'))
  if (draft.subtotal.trim() === '' || !Number.isFinite(base) || base < 0) {
    found.push(tt('Základ musí byť číslo, nula alebo viac.'))
  }
  if (draft.status === 'void' && draft.notes.trim() === '') {
    found.push(tt('Zrušenie faktúry potrebuje dôvod - napíšte ho do poznámky.'))
  }
  if (draft.notes.length > NOTES_LIMIT) {
    found.push(tt('Poznámka je príliš dlhá.'))
  }
  return found
}

/**
 * What to PATCH: only the fields somebody actually changed.
 *
 * <p><b>The same rule as every other patch on this stack, and here it matters twice over.</b> Sending
 * an unchanged `invoiceNumber` back would stamp an edit on a tax document nobody edited, and
 * `OneInvoicePerNumberTest` guards that number's uniqueness - so re-sending it is a write that can be
 * refused for no reason.
 *
 * <p>Returns `null` when nothing moved, so the button can say so rather than sending an empty body.
 */
export function invoiceEditBody(
  draft: InvoiceEditDraft,
  stored: InvoiceEditDraft,
): Record<string, unknown> | null {
  const body: Record<string, unknown> = {}
  if (draft.invoiceNumber.trim() !== stored.invoiceNumber.trim()) {
    body.invoiceNumber = draft.invoiceNumber.trim()
  }
  if (draft.variableSymbol.trim() !== stored.variableSymbol.trim()) {
    // Blank clears it rather than writing an empty string: a franchise with no variable symbol has
    // none, and `''` on a document is not the same fact as absent.
    body.variableSymbol = draft.variableSymbol.trim() === '' ? null : draft.variableSymbol.trim()
  }
  if (draft.deliveryDate !== stored.deliveryDate) {
    body.deliveryDate = draft.deliveryDate === '' ? null : draft.deliveryDate
  }
  if (draft.dueAt !== stored.dueAt) {
    body.dueAt = draft.dueAt === '' ? null : draft.dueAt
  }
  if (draft.vatRatePct.trim() !== stored.vatRatePct.trim()) {
    body.vatRatePct = Number(draft.vatRatePct)
  }
  /* §3.121 - the state and the note.
   *
   * **A cleared note sends an explicit `null`.** `Patches.column` reads absent as *leave it* and null
   * as *clear it*, so omitting a note somebody deleted would leave the old text on the document.
   * §10A.234 refuses the same null on `status`, where the column is `NOT NULL` - the asymmetry is the
   * schema's, not a preference. */
  /* §3.126 - **the three money figures, derived from the one that was typed.**
   *
   * Sent together whenever the base or the rate moved, because they are one fact: a document whose VAT
   * is not its rate applied to its base is not defensible, and sending the base alone would leave the
   * other two saying what they said before. Legacy PATCHes all three from the same arithmetic.
   *
   * **Compared as cents**, not as the typed string: `12.50` and `12.5` are the same money and re-sending
   * a column nobody changed is what §3.5's rule exists to stop. */
  const derived = derivedTotals(draft.subtotal, draft.vatRatePct)
  const storedDerived = derivedTotals(stored.subtotal, stored.vatRatePct)
  if (derived !== null && (storedDerived === null || derived.subtotalCents !== storedDerived.subtotalCents
      || derived.vatAmountCents !== storedDerived.vatAmountCents)) {
    body.subtotal = derived.subtotalCents / 100
    body.vatAmount = derived.vatAmountCents / 100
    body.amount = derived.amountCents / 100
  }
  if (draft.status !== stored.status) body.status = draft.status
  if (draft.notes.trim() !== stored.notes.trim()) {
    body.notes = draft.notes.trim() === '' ? null : draft.notes.trim()
  }
  return Object.keys(body).length === 0 ? null : body
}
